// PipelineRunDetailSheet — Sheet rica de detalhe de um pipeline_run.
//
// Substitui a navegacao pra rota /pipeline-runs/:id (PipelineRunDetailPanel)
// que era um "outro Kanban dentro do Kanban", confuso pro user. Agora click
// numa run no modal de pipeline OU no chip do board abre essa Sheet.
//
// Conteudo "tudo num olhar":
//   - Header: code do run + status + status semantic label
//   - Demand atrelada (code + title + status)
//   - Timing: started/completed/duration
//   - Métricas: tasks_planned/created/done/failed/cancelled, success_rate
//   - DAG steps com status agregado por etapa (lista, NAO Kanban duplicado)
//   - Tasks por step (lista compacta com status)
//   - Execution log (executions kind 'handler' do endpoint, filtradas pelas
//     tasks do run)
//
// PERF-06 (correção UX v2): dados via endpoints /v1 (api.getDemand +
// api.getDemandExecutions) — ZERO supabase.from() no caminho novo. As tasks
// vêm do endpoint da demanda e são filtradas client-side por
// pipeline_run_id === run.id (runs típicos têm 8-30 tasks; o endpoint pagina
// server-side, sem o cap de 1000 do PostgREST). Fallback pré-restart (404 na
// rota nova) → queries supabase antigas isoladas em fetchRunDataLegacy /
// fetchRunDemandLegacy (REMOVER PÓS-RESTART).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/Sheet';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { TooltipProvider } from '@/components/ui/Tooltip';
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  PauseCircle,
  Ban,
  Workflow,
  Wrench,
  Layers,
  ExternalLink,
  Sparkles,
  Columns3,
  ListChecks,
} from 'lucide-react';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { getPipelineRun, type PipelineRunSummary } from '@/lib/api-pipelines';
import { runStatusLabel, runStatusTone } from '@/lib/pipeline-counts';
import { cn } from '@/lib/utils';
import { SheetErrorBoundary } from './SheetErrorBoundary';
import { TaskCard } from './tasks/TaskCard';
import { TaskDetailSheet } from './tasks/TaskDetailSheet';
import { backendToTaskUI, STATUS_LABEL, emptyTasksByStatus, type TaskStatus, type TaskUI } from './tasks/types';
import { formatDurationHuman } from './tasks/format';
import { fmtNumber } from '@/lib/format';
import { api, ApiError, type BackendTask } from '@/lib/api';

interface Props {
  runId: string | null;
  open: boolean;
  onClose: () => void;
}

// P1.12 Frente K — TaskBoard real:
// Shape rica o suficiente pra montar TaskUI via backendToTaskUI(). Antes
// usava shape skinny só pra renderizar lista; agora as colunas usam TaskCard
// canonical (mesmo componente do MCTasksPage Kanban).
type RunTask = BackendTask;
interface DemandRow {
  id: string;
  code: string | null;
  title: string | null;
  status: string;
  acceptance_criteria: unknown[] | null;
}
interface ExecRow {
  id: string;
  handler_name: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error: string | null;
}
interface ConfigStep {
  id: string;
  name?: string;
  kind?: string;
  target_slug?: string;
  handler_name?: string;
}

/* ── Fallback LEGACY pré-restart — REMOVER PÓS-RESTART DO BACKEND ───────────
 *
 * PERF-06: enquanto o backend não reinicia com GET /v1/demands/:id/executions,
 * o endpoint responde 404 → estas funções reproduzem as queries supabase
 * ANTIGAS do sheet (tasks por pipeline_run_id, handler_executions por
 * pipeline_run_id, demands por id). Mesmo padrão do
 * src/pages/mc/demand-executions-legacy.ts (lá a query é demand-scoped com
 * cascateamento de 5 tabelas; aqui o run-scoped direto é mais fiel ao
 * comportamento anterior do sheet). Quando o restart acontecer, deletar este
 * bloco e os branches de 404 em reloadRunData/reloadDemand.
 */
async function fetchRunDataLegacy(
  runId: string,
  tenantId: string | null,
): Promise<{ tasks: RunTask[]; executions: ExecRow[] }> {
  let qt = supabase
    .from('tasks')
    .select('*')
    .eq('pipeline_run_id', runId);
  if (tenantId) qt = qt.eq('tenant_id', tenantId);
  const tasksRes = await qt.order('created_at', { ascending: true });

  let qe = supabase
    .from('handler_executions')
    .select('id, handler_name, status, started_at, completed_at, duration_ms, error')
    .eq('pipeline_run_id', runId);
  if (tenantId) qe = qe.eq('tenant_id', tenantId);
  const execRes = await qe.order('started_at', { ascending: true }).limit(80);

  return {
    tasks: ((tasksRes.data ?? []) as unknown) as RunTask[],
    executions: ((execRes.data ?? []) as unknown) as ExecRow[],
  };
}

/** REMOVER PÓS-RESTART — par do fetchRunDataLegacy pra demand do run. */
async function fetchRunDemandLegacy(
  demandId: string,
  tenantId: string | null,
): Promise<DemandRow | null> {
  let q = supabase
    .from('demands')
    .select('id, code, title, status, acceptance_criteria')
    .eq('id', demandId);
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data } = await q.maybeSingle();
  return ((data ?? null) as unknown) as DemandRow | null;
}

// P1.13 F4: RUN_STATUS_TONE/LABEL extraidos pra @/lib/pipeline-counts
// (runStatusLabel/runStatusTone). Centralizados pra exibir status canonical
// de pipeline_runs.status em TODA a UI sem inferir a partir de tasks_done.
const TASK_STATUS_TONE: Record<string, string> = {
  pending: 'text-muted-foreground',
  queued: 'text-muted-foreground',
  running: 'text-blue-600 dark:text-blue-400',
  done: 'text-green-600 dark:text-green-400',
  failed: 'text-red-600 dark:text-red-400',
  cancelled: 'text-muted-foreground',
};
const KIND_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  agent: Workflow,
  skill: Wrench,
  task_orchestration: Layers,
};

function StatusIcon({ status }: { status: string }) {
  if (status === 'done' || status === 'completed') return <CheckCircle2 className="h-3 w-3" />;
  if (status === 'failed') return <XCircle className="h-3 w-3" />;
  if (status === 'running' || status === 'executing' || status === 'decomposing') return <Clock className="h-3 w-3 animate-pulse" />;
  if (status === 'blocked') return <PauseCircle className="h-3 w-3" />;
  if (status === 'cancelled') return <Ban className="h-3 w-3" />;
  return <AlertCircle className="h-3 w-3" />;
}

function fmtDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${fmtNumber(s, 1)}s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s % 60);
  return rs > 0 ? `${m}m ${rs}s` : `${m}m`;
}

function fmtRunDuration(start: string | null, end: string | null): string {
  if (!start) return '—';
  const startT = new Date(start).getTime();
  const endT = end ? new Date(end).getTime() : Date.now();
  return fmtDuration(endT - startT);
}

export function PipelineRunDetailSheet({ runId, open, onClose }: Props) {
  // W2.3 — tenant_id só é usado no caminho LEGACY (queries supabase de
  // fallback pré-restart): defesa em profundidade sobre a RLS. O caminho novo
  // (/v1) já é tenant-scoped server-side pelo token.
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  const [run, setRun] = useState<PipelineRunSummary | null>(null);
  const [tasks, setTasks] = useState<RunTask[]>([]);
  const [demand, setDemand] = useState<DemandRow | null>(null);
  const [executions, setExecutions] = useState<ExecRow[]>([]);
  const [loading, setLoading] = useState(false);
  // P1.12 Frente K — agrupamento da TaskBoard. 'step' = colunas por
  // pipeline_step_id (default canonical, exibe DAG); 'status' = colunas
  // pendente/running/done/issues (espelha MCTasksPage Kanban). Toggle
  // persistido só em memória (sem URL state — sheet é efêmero).
  const [boardGroup, setBoardGroup] = useState<'step' | 'status'>('step');
  // Click em card abre TaskDetailSheet (mesmo componente canonical do
  // MCTasksPage). State local pra não interferir com seleção da sheet pai.
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const reloadRun = useCallback(async () => {
    if (!runId) return;
    try {
      const r = await getPipelineRun(runId);
      setRun(r);
    } catch { /* keep prev */ }
  }, [runId]);

  // PERF-06 — tasks + execution log num round só via GET /v1/demands/:id/
  // executions (api.getDemandExecutions). O run NÃO tem endpoint dedicado de
  // tasks; o endpoint da demanda retorna TODAS as tasks dela (incluindo
  // pipeline_run_id/pipeline_step_id) e filtramos client-side pelo run.
  // Execution log: itens kind 'handler' de `executions[]` cujo task_id
  // pertence às tasks do run (substitui handler_executions.eq pipeline_run_id
  // — mesmo conjunto pra handlers ancorados em task do run).
  const reloadRunData = useCallback(async (demandId: string) => {
    if (!runId) return;
    try {
      const res = await api.getDemandExecutions(demandId, { limit: 1000 });
      const runTasks = ((res.tasks as unknown) as RunTask[])
        .filter((t) => t.pipeline_run_id === runId)
        .sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0));
      setTasks(runTasks);
      const runTaskIds = new Set(runTasks.map((t) => t.id));
      const execs: ExecRow[] = res.executions
        .filter((e) => e.kind === 'handler' && e.task_id !== null && runTaskIds.has(e.task_id))
        .map((e) => ({
          id: e.id,
          handler_name: e.handler_name ?? '—',
          status: e.status,
          started_at: e.started_at,
          // Endpoint expõe finished_at (canonical); ExecRow preserva o nome
          // completed_at do shape interno antigo.
          completed_at: e.finished_at,
          duration_ms: e.duration_ms,
          error: e.error,
        }))
        .sort((a, b) => (a.started_at ?? '') < (b.started_at ?? '') ? -1 : (a.started_at ?? '') > (b.started_at ?? '') ? 1 : 0)
        .slice(0, 80);
      setExecutions(execs);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Backend pré-restart (rota /executions ainda não existe) → legacy.
        try {
          const legacy = await fetchRunDataLegacy(runId, tenantId);
          setTasks(legacy.tasks);
          setExecutions(legacy.executions);
        } catch (legacyErr) {
          console.error('[PipelineRunDetailSheet] fallback legacy falhou:', legacyErr);
        }
        return;
      }
      console.error('[PipelineRunDetailSheet] getDemandExecutions falhou:', err);
      /* keep prev */
    }
  }, [runId, tenantId]);

  const reloadDemand = useCallback(async (demandId: string) => {
    try {
      const d = await api.getDemand(demandId);
      setDemand({
        id: d.id,
        code: d.code ?? null,
        title: d.title ?? null,
        status: d.status,
        acceptance_criteria: d.acceptance_criteria ?? null,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // 404 = demand inexistente OU backend sem a rota → legacy resolve os 2.
        try {
          setDemand(await fetchRunDemandLegacy(demandId, tenantId));
        } catch {
          setDemand(null);
        }
        return;
      }
      /* keep prev */
    }
  }, [tenantId]);

  useEffect(() => {
    let mounted = true;
    if (!open || !runId) {
      Promise.resolve().then(() => {
        if (!mounted) return;
        setRun(null); setTasks([]); setDemand(null); setExecutions([]);
      });
      return () => { mounted = false; };
    }
    Promise.resolve().then(() => {
      if (!mounted) return;
      setLoading(true);
      void reloadRun().finally(() => { if (mounted) setLoading(false); });
    });
    return () => { mounted = false; };
  }, [open, runId, reloadRun]);

  // Demand + tasks/execution log dependem do demand_id do run (chega no
  // primeiro reloadRun). demand_id é imutável no run → efeito roda 1x por run.
  useEffect(() => {
    if (!open || !run?.demand_id) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      void reloadDemand(run.demand_id);
      void reloadRunData(run.demand_id);
    });
    return () => {
      cancelled = true;
    };
  }, [open, run?.demand_id, reloadDemand, reloadRunData]);

  // W2.5 — captura erros que escapam do Error Boundary React (event handlers,
  // promises não-tratadas, async em useEffect). Loga no console com prefixo
  // identificável (mesmo padrão do TaskDetailSheet).
  useEffect(() => {
    if (!open || !runId) return;
    function handleError(e: ErrorEvent) {
      console.error('[PipelineRunDetailSheet] window error · run', runId, e.error ?? e.message, e);
    }
    function handleRejection(e: PromiseRejectionEvent) {
      console.error('[PipelineRunDetailSheet] unhandled rejection · run', runId, e.reason, e);
    }
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [open, runId]);

  useSupabaseRealtime(
    { table: 'pipeline_runs', filter: runId ? `id=eq.${runId}` : undefined },
    () => { reloadRun(); },
  );
  // Realtime continua nas TABELAS (sinal de mudança); o reload em si vai
  // pelo endpoint /v1 (tasks + execution log num round só).
  useSupabaseRealtime(
    { table: 'tasks', filter: runId ? `pipeline_run_id=eq.${runId}` : undefined },
    () => { if (run?.demand_id) reloadRunData(run.demand_id); },
  );
  useSupabaseRealtime(
    { table: 'handler_executions', filter: runId ? `pipeline_run_id=eq.${runId}` : undefined },
    () => { if (run?.demand_id) reloadRunData(run.demand_id); },
  );

  const steps = useMemo<ConfigStep[]>(() => {
    if (!run) return [];
    return ((run.config?.['steps'] as ConfigStep[] | undefined) ?? []);
  }, [run]);

  const tasksByStep = useMemo(() => {
    const map = new Map<string, RunTask[]>();
    for (const s of steps) map.set(s.id, []);
    map.set('__unassigned__', []);
    for (const t of tasks) {
      const k = t.pipeline_step_id ?? '__unassigned__';
      const arr = map.get(k) ?? [];
      arr.push(t);
      map.set(k, arr);
    }
    return map;
  }, [steps, tasks]);

  const stepStatusMap = useMemo<Map<string, 'pending' | 'running' | 'done' | 'failed'>>(() => {
    const out = new Map<string, 'pending' | 'running' | 'done' | 'failed'>();
    for (const s of steps) {
      const ts = tasksByStep.get(s.id) ?? [];
      if (ts.length === 0) { out.set(s.id, 'pending'); continue; }
      if (ts.some((t) => t.status === 'failed')) out.set(s.id, 'failed');
      else if (ts.some((t) => t.status === 'running')) out.set(s.id, 'running');
      else if (ts.every((t) => t.status === 'done' || t.status === 'cancelled')) out.set(s.id, 'done');
      else out.set(s.id, 'pending');
    }
    return out;
  }, [steps, tasksByStep]);

  // Status semantica: pending != queued != running.
  // - pending: criada, ainda nao foi pra fila
  // - queued: na fila do dispatcher, aguardando worker pegar
  // - running: worker executando agora
  const totalTasks = tasks.length;
  const tasksDone = tasks.filter((t) => t.status === 'done').length;
  const tasksFailed = tasks.filter((t) => t.status === 'failed').length;
  const tasksCancelled = tasks.filter((t) => t.status === 'cancelled').length;
  const tasksRunning = tasks.filter((t) => t.status === 'running').length;
  const tasksPending = tasks.filter((t) => t.status === 'pending').length;
  // 'queued' não está em BackendTaskStatus canonico (apenas
  // pending/running/retry/done/failed/cancelled/paused). Mantido como cast
  // defensivo caso backend exponha estado transient — soma 0 no shape atual.
  const tasksQueued = tasks.filter((t) => (t.status as string) === 'queued').length;

  // Decomposer reasoning: backend pode populear no config do run quando
  // disponivel (ex: pipeline_runs.config.decomposer_reasoning ou
  // .decomposer_output.reasoning). Renderiza apenas se houver string nao vazia.
  const decomposerReasoning = useMemo<string | null>(() => {
    const cfg = run?.config ?? {};
    const direct = cfg['decomposer_reasoning'];
    if (typeof direct === 'string' && direct.trim().length > 0) return direct;
    const out = cfg['decomposer_output'];
    if (out && typeof out === 'object' && !Array.isArray(out)) {
      const r = (out as Record<string, unknown>)['reasoning'];
      if (typeof r === 'string' && r.trim().length > 0) return r;
    }
    return null;
  }, [run]);

  // Pipeline slug/version no run. Backend pode populear no config (snapshot
  // canonico imutavel pra audit), senao caimos pra o id curto.
  const pipelineSlug = useMemo<string | null>(() => {
    const v = run?.config?.['pipeline_slug'];
    return typeof v === 'string' ? v : null;
  }, [run]);
  const pipelineVersionLabel = useMemo<string | null>(() => {
    const v = run?.config?.['pipeline_version'] ?? run?.config?.['version'];
    return typeof v === 'string' ? v : null;
  }, [run]);

  const runTone = run ? runStatusTone(run.status) : '';
  const runLabel = run ? runStatusLabel(run.status) : '';

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      {/* TooltipProvider obrigatório: componentes filhos (ex.: TaskCard renderizado dentro do Sheet) usam <Tooltip>, que crasha com "Tooltip must be used within TooltipProvider" se não estiver wrappado. */}
      <TooltipProvider>
      <SheetContent className="w-[92vw] sm:w-[640px] max-w-full" aria-describedby="pipeline-run-detail-desc">
        <SheetHeader className="shrink-0 border-b border-raios-border pb-4">
          {!run ? (
            <>
              <SheetTitle>Carregando run…</SheetTitle>
              {/* SheetDescription sempre presente (Radix exige Description
                  linkada via aria-describedby; sem ela dispara warning). */}
              <SheetDescription id="pipeline-run-detail-desc" className="sr-only">
                Detalhe de um pipeline run carregando.
              </SheetDescription>
            </>
          ) : (
            <>
              {/* Identificacao canonica do run: pipeline_slug + versao + run code.
                  Antes era so "run #abcd1234" — pobre. Agora user ve de qual
                  pipeline e versao a run pertence. */}
              <div className="flex items-center gap-2 flex-wrap">
                {pipelineSlug && (
                  <Badge variant="info" className="font-mono text-[10px]">
                    {pipelineSlug}
                  </Badge>
                )}
                {pipelineVersionLabel && (
                  <Badge variant="outline" className="font-mono text-[10px]">
                    v{pipelineVersionLabel}
                  </Badge>
                )}
                <Badge variant="outline" className="font-mono text-[10px]">
                  run #{run.id.slice(0, 8)}
                </Badge>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${runTone}`}>
                  <StatusIcon status={run.status} /> {runLabel}
                </span>
                {run.business_id && (
                  <Badge variant="outline" className="text-[10px]" title="Business associada ao run">
                    biz {run.business_id.slice(0, 8)}
                  </Badge>
                )}
              </div>
              <SheetTitle className="text-base">
                {demand?.title ?? '(demand sem título)'}
              </SheetTitle>
              <SheetDescription id="pipeline-run-detail-desc">
                Pipeline run · iniciado{' '}
                {run.started_at
                  ? new Date(run.started_at).toLocaleString('pt-BR')
                  : '—'}{' '}
                · duração {fmtRunDuration(run.started_at, run.completed_at)}
                {/* P1.12 Frente T — ETA canonical em segundos. Renderizado
                    apenas quando run popula o campo (v3.0.0+). */}
                {typeof run.estimated_duration_seconds === 'number' &&
                  run.estimated_duration_seconds > 0 && (
                    <>
                      {' · ETA '}
                      <span className="font-mono">
                        {formatDurationHuman(run.estimated_duration_seconds)}
                      </span>
                    </>
                  )}
                {/* P1.12 Frente F — backward_depth_current. Mostra quando
                    incrementado (não-null e > 0). Útil pra debug visual de
                    runs com loops Jeremias-rejeita. */}
                {typeof run.backward_depth_current === 'number' &&
                  run.backward_depth_current > 0 && (
                    <>
                      {' · backwards '}
                      <span className="font-mono">{run.backward_depth_current}</span>
                    </>
                  )}
              </SheetDescription>
            </>
          )}
        </SheetHeader>

        {/* W2.5 — error boundary envolvendo o conteúdo (antes a Sheet quebrava
            a TELA inteira ao crashar no render). W2.6 — viewport simples
            (overflow-y-auto + min-w-0) em vez de ScrollArea: o viewport do
            ScrollArea usa table-layout e neutraliza o overflow-x-auto do
            Kanban interno (TaskBoard horizontal). Mesmo padrão de TaskDetailSheet. */}
        <SheetErrorBoundary resetKey={runId} logLabel="PipelineRunDetailSheet">
        <div className="flex-1 overflow-y-auto min-w-0 px-6 py-4">
          {loading && !run ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : run ? (
            <div className="space-y-5">
              {/* Demand atrelada — clicavel pra abrir Mission Control de demands.
                  Como nao ha rota standalone /demands/:id, redireciona pra
                  /mc/demands (panel detalhado abre la). */}
              {demand && (
                <Card className="p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Demand</p>
                    <Link
                      to="/mc/demands"
                      className="inline-flex items-center gap-1 text-[11px] text-brand hover:underline"
                      title="Abrir Mission Control · Demands"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Abrir
                    </Link>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="font-mono text-[10px]">{demand.code ?? demand.id.slice(0, 8)}</Badge>
                    <Badge variant="outline" className="text-[10px]">{demand.status}</Badge>
                    {Array.isArray(demand.acceptance_criteria) && (
                      <span className="text-[11px] text-muted-foreground">
                        {demand.acceptance_criteria.length} {demand.acceptance_criteria.length === 1 ? 'critério' : 'critérios'} de aceitação
                      </span>
                    )}
                  </div>
                  {demand.title && (
                    <p className="text-sm mt-2">{demand.title}</p>
                  )}
                </Card>
              )}

              {/* Métricas tasks — pending/queued/running SEPARADOS (semantica
                  precisa: pending != queued != running). Sem fusao "pendentes". */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Tasks ({totalTasks})</p>
                <div className="grid grid-cols-3 gap-2">
                  <MetricMini label="Em execução" value={tasksRunning} tone="text-blue-600 dark:text-blue-400" />
                  <MetricMini label="Na fila" value={tasksQueued} tone="text-muted-foreground" />
                  <MetricMini label="Pendentes" value={tasksPending} tone="text-muted-foreground" />
                  <MetricMini label="Concluídas" value={tasksDone} tone="text-green-600 dark:text-green-400" />
                  <MetricMini label="Falhas" value={tasksFailed} tone="text-red-600 dark:text-red-400" />
                  <MetricMini label="Canceladas" value={tasksCancelled} tone="text-muted-foreground" />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <MetricMini label="Planned" value={run.tasks_planned ?? 0} />
                  <MetricMini label="Created" value={run.tasks_created ?? 0} />
                  <MetricMini
                    label="Success rate"
                    value={run.success_rate !== null ? `${fmtNumber(run.success_rate * 100, 0)}%` : '—'}
                  />
                </div>
                {run.scope_growth_rate !== null && run.scope_growth_rate !== undefined && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    scope_growth_rate · <span className="font-mono">{fmtNumber(run.scope_growth_rate, 2)}</span>
                  </p>
                )}
              </div>

              {/* Decomposer reasoning — quando o decomposer (step 1 agente+skill)
                  populou raciocinio no config do run. Renderizado como bloco
                  monoespacado com prelinha. Caso ausente, secao nao aparece. */}
              {decomposerReasoning && (
                <Card className="p-3 border-purple-300/60 dark:border-purple-700/60 bg-purple-50/40 dark:bg-purple-900/10">
                  <p className="text-[10px] uppercase tracking-wider text-purple-700 dark:text-purple-300 mb-2 inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Decomposer reasoning
                  </p>
                  <pre className="text-[11px] whitespace-pre-wrap leading-relaxed text-foreground/90 max-h-48 overflow-y-auto font-sans">
                    {decomposerReasoning}
                  </pre>
                </Card>
              )}

              {/* P1.12 Frente K — TaskBoard real do pipeline_run.
                  Modo 'step': N colunas (uma por pipeline_step_id canonical
                  + 'unassigned' se existir tasks órfãs). Cards usam TaskCard
                  canonical (mesmo do MCTasksPage).
                  Modo 'status': 4 colunas (pending/running/done/issues) —
                  espelha Kanban global pra cross-cutting view.
                  Click no card abre TaskDetailSheet (canonical, mesmo
                  componente do MCTasksPage). Toggle persistido só em
                  memória. */}
              {tasks.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Tasks no run ({tasks.length})
                    </p>
                    <div className="inline-flex rounded-md border border-raios-border overflow-hidden">
                      <Button
                        variant={boardGroup === 'step' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setBoardGroup('step')}
                        className="h-7 px-2 rounded-none text-[11px]"
                        aria-label="Agrupar por etapa"
                      >
                        <Columns3 className="h-3 w-3" />
                        Etapa
                      </Button>
                      <Button
                        variant={boardGroup === 'status' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setBoardGroup('status')}
                        className="h-7 px-2 rounded-none text-[11px]"
                        aria-label="Agrupar por status"
                      >
                        <ListChecks className="h-3 w-3" />
                        Status
                      </Button>
                    </div>
                  </div>
                  <PipelineRunTaskBoard
                    group={boardGroup}
                    tasks={tasks}
                    steps={steps}
                    tasksByStep={tasksByStep}
                    stepStatusMap={stepStatusMap}
                    onTaskClick={(t) => setSelectedTaskId(t.id)}
                  />
                </div>
              ) : (
                steps.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                      Etapas ({steps.length})
                    </p>
                    <p className="text-xs text-muted-foreground italic">
                      Tasks ainda não materializadas. O decomposer (etapa 1)
                      criará as tasks para este run quando começar.
                    </p>
                  </div>
                )
              )}

              {/* Execution log */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                  Execution log ({executions.length})
                </p>
                {executions.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Sem eventos.</p>
                ) : (
                  <Card className="p-2.5">
                    <div className="space-y-1 font-mono text-[11px] max-h-64 overflow-y-auto">
                      {executions.map((e) => (
                        <div key={e.id} className="flex items-start gap-2 py-1 border-b border-raios-border/30 last:border-0">
                          <span className="text-muted-foreground shrink-0 w-16 tabular-nums">
                            {e.started_at ? new Date(e.started_at).toLocaleTimeString('pt-BR') : '—'}
                          </span>
                          <span className={`shrink-0 ${TASK_STATUS_TONE[e.status === 'completed' ? 'done' : e.status] ?? ''}`}>
                            <StatusIcon status={e.status === 'completed' ? 'done' : e.status} />
                          </span>
                          <Badge variant="outline" className="text-[10px] shrink-0">{e.status}</Badge>
                          <span className="font-semibold truncate">{e.handler_name}</span>
                          {e.duration_ms !== null && e.duration_ms > 0 && (
                            <span className="text-muted-foreground shrink-0 tabular-nums">{fmtDuration(e.duration_ms)}</span>
                          )}
                          {e.error && (
                            <span className="text-red-600 dark:text-red-400 truncate" title={e.error}>
                              {e.error.slice(0, 80)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            </div>
          ) : null}
        </div>
        </SheetErrorBoundary>
      </SheetContent>
      {/* P1.12 Frente K — TaskDetailSheet aninhada. Abre por cima do Sheet
          de run sem navegar (mesmo padrão do PipelineDetailModal → Run). */}
      <TaskDetailSheet
        taskId={selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
      />
      </TooltipProvider>
    </Sheet>
  );
}

function MetricMini({ label, value, tone }: { label: string; value: number | string; tone?: string }) {
  return (
    <div className="rounded-md border border-raios-border bg-raios-elevated/30 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-lg font-mono tabular-nums ${tone ?? ''}`}>{value}</p>
    </div>
  );
}

/* ── PipelineRunTaskBoard ────────────────────────────────────────────────────
 *
 * P1.12 Frente K — TaskBoard real do pipeline_run.
 *
 * - Modo 'step': uma coluna por pipeline_step_id (ordem do config), mais
 *   coluna 'unassigned' se houver tasks órfãs. Header da coluna mostra
 *   nome da etapa + agregado (n tasks) + status agregado.
 * - Modo 'status': 4 colunas canonical (pending/running/done/issues),
 *   espelhando MCTasksPage Kanban global.
 *
 * Cards sempre usam `TaskCard` (mesmo do MCTasksPage). Click chama
 * `onTaskClick` propagado pra abrir TaskDetailSheet acima da Sheet pai.
 *
 * Não importa TaskBoard puro do tasks/ porque o agrupamento aqui é por
 * step (não-status), e TaskBoard expõe colunas-por-status fixas. Reusa
 * TaskCard (canonical) sem duplicar visual de card.
 */

interface RunBoardProps {
  group: 'step' | 'status';
  tasks: BackendTask[];
  steps: ConfigStep[];
  tasksByStep: Map<string, BackendTask[]>;
  stepStatusMap: Map<string, 'pending' | 'running' | 'done' | 'failed'>;
  onTaskClick: (task: TaskUI) => void;
}

const STEP_STATUS_TONE: Record<'pending' | 'running' | 'done' | 'failed', string> = {
  pending: 'bg-raios-muted',
  running: 'bg-raios-accent',
  done: 'bg-raios-success',
  failed: 'bg-raios-danger',
};

function PipelineRunTaskBoard({
  group,
  tasks,
  steps,
  tasksByStep,
  stepStatusMap,
  onTaskClick,
}: RunBoardProps) {
  if (group === 'status') {
    // Modo status — espelha shape do MCTasksPage.
    const byStatus = emptyTasksByStatus();
    for (const t of tasks) {
      const ui = backendToTaskUI(t);
      const arr = byStatus[ui.status as TaskStatus] ?? [];
      arr.push(ui);
      byStatus[ui.status as TaskStatus] = arr;
    }
    // Ordem canonical do MCTasksPage: pending → running → done → issues
    // (failed/retry/cancelled colapsados). Aqui mostramos as 4 principais.
    const statusOrder: TaskStatus[] = ['pending', 'running', 'done', 'failed', 'retry', 'cancelled', 'paused'];
    const visibleStatuses = statusOrder.filter((s) => (byStatus[s]?.length ?? 0) > 0);
    return (
      <div className="flex gap-3 overflow-x-auto pb-2 items-start">
        {visibleStatuses.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Sem tasks pra agrupar.</p>
        ) : (
          visibleStatuses.map((status) => (
            <BoardColumn
              key={status}
              title={STATUS_LABEL[status]}
              count={byStatus[status]?.length ?? 0}
              accentClass={STATUS_DOT_CLASS[status]}
            >
              {(byStatus[status] ?? []).map((task) => (
                <TaskCard key={task.id} task={task} onClick={onTaskClick} />
              ))}
            </BoardColumn>
          ))
        )}
      </div>
    );
  }

  // Modo step — colunas em ordem do config + unassigned no fim se existir.
  const unassigned = tasksByStep.get('__unassigned__') ?? [];
  return (
    <div className="flex gap-3 overflow-x-auto pb-2 items-start">
      {steps.map((s) => {
        const stepTasks = tasksByStep.get(s.id) ?? [];
        const aggStatus = stepStatusMap.get(s.id) ?? 'pending';
        const KindIcon = KIND_ICON[s.kind ?? 'agent'] ?? Workflow;
        return (
          <BoardColumn
            key={s.id}
            title={s.name ?? s.id}
            subtitle={
              <span className="inline-flex items-center gap-1">
                <KindIcon className="h-3 w-3" />
                <span className="font-mono truncate">
                  {s.target_slug ?? s.handler_name ?? s.kind ?? '—'}
                </span>
              </span>
            }
            count={stepTasks.length}
            accentClass={STEP_STATUS_TONE[aggStatus]}
          >
            {stepTasks.length === 0 ? (
              <p className="text-[11px] text-muted-foreground italic px-1 py-2">
                Sem tasks nesta etapa.
              </p>
            ) : (
              stepTasks.map((t) => (
                <TaskCard key={t.id} task={backendToTaskUI(t)} onClick={onTaskClick} />
              ))
            )}
          </BoardColumn>
        );
      })}
      {/* Unassigned — etapa "fantasma" pra tasks sem pipeline_step_id. */}
      {unassigned.length > 0 && (
        <BoardColumn
          title="Sem etapa"
          subtitle={<span className="text-yellow-700 dark:text-yellow-400">orphan</span>}
          count={unassigned.length}
          accentClass="bg-yellow-500"
        >
          {unassigned.map((t) => (
            <TaskCard key={t.id} task={backendToTaskUI(t)} onClick={onTaskClick} />
          ))}
        </BoardColumn>
      )}
    </div>
  );
}

const STATUS_DOT_CLASS: Record<TaskStatus, string> = {
  pending: 'bg-raios-warning',
  running: 'bg-raios-accent',
  done: 'bg-raios-success',
  failed: 'bg-raios-danger',
  retry: 'bg-raios-warning',
  cancelled: 'bg-raios-muted',
  paused: 'bg-raios-muted',
};

interface BoardColumnProps {
  title: string;
  subtitle?: React.ReactNode;
  count: number;
  accentClass: string;
  children: React.ReactNode;
}

function BoardColumn({ title, subtitle, count, accentClass, children }: BoardColumnProps) {
  return (
    <section className="flex flex-col min-w-[260px] w-[260px] rounded-lg bg-raios-surface/40 border border-raios-elevated/30 shrink-0">
      <header className="flex items-start justify-between gap-2 px-3 py-2 border-b border-raios-elevated/30">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn('h-2 w-2 rounded-full shrink-0', accentClass)} aria-hidden />
            <h3 className="text-xs font-semibold uppercase tracking-wide truncate">{title}</h3>
          </div>
          {subtitle && (
            <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{subtitle}</div>
          )}
        </div>
        <span className="text-[11px] tabular-nums font-medium text-raios-text shrink-0">
          {count}
        </span>
      </header>
      <div className="flex-1 flex flex-col gap-2 p-2 max-h-[60vh] overflow-y-auto">
        {children}
      </div>
    </section>
  );
}
